import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import FilterPanel from "@/pages/category/components/FilterPanel";
import MovieGrid from "@/components/feature/MovieGrid";
import SectionHeading from "@/components/feature/SectionHeading";
import { SORT_OPTIONS, getCategoryInfo } from "@/lib/categories";
import { PAGE_SIZE, filterMovies, getHotMovies } from "@/lib/movies";
import { useSeo } from "@/hooks/useSeo";

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const genre = type ? decodeURIComponent(type) : "全部";
  const info = getCategoryInfo(genre);
  const sortParam = searchParams.get("sort") || "latest";

  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [sort, setSort] = useState(sortParam);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setRegion("全部");
    setYear("全部");
    setPage(1);
  }, [genre]);

  useEffect(() => {
    setSort(sortParam);
  }, [sortParam]);

  useSeo({
    title: `${info.name}${genre === "全部" ? "" : "电影"} - 免费看大片在线观看平台`,
    description: `${info.description.slice(0, 80)}在免费看大片按地区与年份筛选${info.name}影片，支持评分与热度排序，快速找到想看的高清电影。`,
    keywords: `${info.name},${info.name}电影,${info.name}在线观看,免费看大片,高清电影`,
  });

  const results = useMemo(
    () => filterMovies({ genre, region, year, sort }),
    [genre, region, year, sort],
  );

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageMovies = results.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const hotMovies = getHotMovies(5);

  const handleFilterChange = (patch: {
    genre?: string;
    region?: string;
    year?: string;
    sort?: string;
  }) => {
    if (patch.genre && patch.genre !== genre) {
      navigate(`/category/${encodeURIComponent(patch.genre)}`);
      return;
    }
    if (patch.region !== undefined) setRegion(patch.region);
    if (patch.year !== undefined) setYear(patch.year);
    if (patch.sort !== undefined) setSort(patch.sort);
    setPage(1);
  };

  const handleReset = () => {
    setRegion("全部");
    setYear("全部");
    setSort("latest");
    setPage(1);
  };

  const pageNumbers = Array.from({ length: totalPages }, (_, index) => index + 1);

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 py-6 md:px-8 md:py-10">
      <nav aria-label="面包屑导航" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-300">
          首页
        </Link>
        <i className="ri-arrow-right-s-line text-foreground-600" />
        <Link to="/category/全部" className="transition-colors hover:text-primary-300">
          电影分类
        </Link>
        <i className="ri-arrow-right-s-line text-foreground-600" />
        <span className="text-foreground-200">{info.name}</span>
      </nav>

      <div className="mt-4 overflow-hidden rounded-lg border border-background-200/70 bg-gradient-to-r from-primary-950/70 via-background-100 to-background-100 p-5 md:p-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-500 text-background-50">
                <i className={`${info.icon} text-xl`} />
              </span>
              <h1 className="font-heading text-2xl font-semibold text-foreground-50 md:text-3xl">
                {info.name}
              </h1>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-foreground-400">{info.description}</p>
          </div>
          <div className="flex shrink-0 gap-6 md:gap-8">
            <div>
              <p className="font-heading text-2xl font-semibold text-accent-400">{results.length}</p>
              <p className="mt-1 text-xs text-foreground-500">当前结果</p>
            </div>
            <div>
              <p className="font-heading text-2xl font-semibold text-foreground-100">
                {totalPages}
              </p>
              <p className="mt-1 text-xs text-foreground-500">页数</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[248px_minmax(0,1fr)] lg:gap-7">
        <aside>
          <FilterPanel
            genre={genre}
            region={region}
            year={year}
            onChange={handleFilterChange}
            onReset={handleReset}
          />
        </aside>

        <div className="min-w-0">
          <div className="flex flex-col gap-3 rounded-lg border border-background-200/70 bg-background-100/60 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-foreground-500 md:text-sm">
              共
              <span className="mx-1 font-heading text-sm font-semibold text-accent-400 md:text-base">
                {results.length}
              </span>
              部影片
              <span className="ml-2 text-foreground-600">
                （{genre} · {region} · {year}）
              </span>
            </p>
            <div className="flex items-center gap-2">
              <span className="whitespace-nowrap text-xs text-foreground-500">排序</span>
              <div className="relative">
                <select
                  value={sort}
                  onChange={(event) => handleFilterChange({ sort: event.target.value })}
                  aria-label="排序方式"
                  className="cursor-pointer appearance-none rounded-md border border-background-200 bg-background-50 py-1.5 pl-3 pr-8 text-xs text-foreground-200 outline-none transition-colors hover:border-primary-500/50 focus:border-primary-500 sm:text-sm"
                >
                  {SORT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <i className="ri-arrow-down-s-line pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-sm text-foreground-500" />
              </div>
            </div>
          </div>

          <div className="mt-5">
            {pageMovies.length > 0 ? (
              <>
                <MovieGrid movies={pageMovies} showSynopsis />
                {totalPages > 1 && (
                  <nav
                    aria-label="分页导航"
                    className="mt-9 flex flex-wrap items-center justify-center gap-2"
                  >
                    <button
                      type="button"
                      onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                      disabled={safePage === 1}
                      className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-background-200 bg-background-100 px-3 py-2 text-sm text-foreground-300 transition-colors hover:border-primary-500/50 hover:text-foreground-50 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <i className="ri-arrow-left-s-line text-base" />
                      上一页
                    </button>

                    {pageNumbers.map((number) => (
                      <button
                        key={number}
                        type="button"
                        onClick={() => setPage(number)}
                        aria-current={number === safePage}
                        className={`h-9 min-w-9 cursor-pointer rounded-md px-2 font-heading text-sm transition-colors ${
                          number === safePage
                            ? "bg-primary-500 font-semibold text-background-50"
                            : "border border-background-200 bg-background-100 text-foreground-300 hover:border-primary-500/50 hover:text-foreground-50"
                        }`}
                      >
                        {number}
                      </button>
                    ))}

                    <button
                      type="button"
                      onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                      disabled={safePage === totalPages}
                      className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-background-200 bg-background-100 px-3 py-2 text-sm text-foreground-300 transition-colors hover:border-primary-500/50 hover:text-foreground-50 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      下一页
                      <i className="ri-arrow-right-s-line text-base" />
                    </button>
                  </nav>
                )}
                <p className="mt-4 text-center text-xs text-foreground-600">
                  第 {safePage} / {totalPages} 页 · 每页 {PAGE_SIZE} 部影片
                </p>
              </>
            ) : (
              <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100/50 px-6 py-14 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200/70 text-foreground-400">
                  <i className="ri-search-eye-line text-2xl" />
                </span>
                <h3 className="mt-4 font-heading text-lg font-medium text-foreground-100">
                  没有找到符合条件的影片
                </h3>
                <p className="mt-2 max-w-md text-sm leading-relaxed text-foreground-500">
                  当前筛选组合下暂无结果，试试放宽地区或年份条件，也可以直接回到全部影片。
                </p>
                <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={handleReset}
                    className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-refresh-line text-base" />
                    重置筛选
                  </button>
                  <Link
                    to="/category/全部"
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm text-foreground-200 transition-colors hover:border-primary-500/60 hover:text-primary-300"
                  >
                    查看全部影片
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {pageMovies.length === 0 && (
        <section className="mt-12">
          <SectionHeading
            eyebrow="Recommended"
            title="换个口味，看看这些热门影片"
            description="这些影片近期播放量最高，也许正好有一部合你心意。"
          />
          <MovieGrid movies={hotMovies} />
        </section>
      )}
    </div>
  );
}